import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { computeLockoutDecision } from '@/lib/subscription';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ONBOARDING_PATHS = ['/onboarding/instagram', '/onboarding/industry', '/onboarding/inspiration-sources', '/onboarding/add-competitors'];
const SUBSCRIPTION_EXEMPT_PATHS = [...ONBOARDING_PATHS, '/subscribe'];

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, isLoggingOut } = useAuth();
  const {
    subscribed,
    subscriptionStatus,
    subscriptionGracePeriodEnd,
    loading: subLoading,
  } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  const isExemptRoute = SUBSCRIPTION_EXEMPT_PATHS.some(p => location.pathname.startsWith(p));

  const lockout = computeLockoutDecision({
    subscribed,
    status: subscriptionStatus,
    gracePeriodEnd: subscriptionGracePeriodEnd,
  });

  useEffect(() => {
    if (!loading && !subLoading && user && !isExemptRoute && lockout.shouldRedirect) {
      const search = lockout.reason ? `?reason=${lockout.reason}` : '';
      navigate(`/subscribe${search}`, { replace: true });
    }
  }, [loading, subLoading, user, isExemptRoute, lockout.shouldRedirect, lockout.reason, navigate]);

  if (loading || (!isExemptRoute && subLoading) || isLoggingOut) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!isExemptRoute && lockout.shouldRedirect) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
